import { type AnimationEvent, type ReactElement, useCallback, useEffect, useState } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { chatCommunicationController } from '../../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import { ChatTabType, type Message } from '../../../store/slices/chat/types';
import { MessageBubble } from '../chat/messages/messageBubble/MessageBubble';
import { MessageInput } from '../messageInput/MessageInput';
import { createMessage } from '../utils';

import styles from './MiniChat.module.css';

const MESSAGE_LIFESPAN = 10_000; // Should be in sync with css variable --core-mini-chat-message-lifespan, including fadeOut animation duration

export interface MiniChatProps {
  casinoId: string;
  currentUserId: string;
  isGameChatBlocked: boolean;
  isGameChatUnavailable: boolean;
  messages: Message[];
  tableId: string;
  userId: string;
  cashierCallback?: () => void;
}

// Flag messages that should have been already displayed
const isMessageExpired = (messageTimestamp: number): boolean => {
  return Date.now() - messageTimestamp >= MESSAGE_LIFESPAN;
};

export const MiniChat = ({
  cashierCallback,
  casinoId,
  currentUserId,
  isGameChatBlocked,
  isGameChatUnavailable,
  messages,
  tableId,
  userId,
}: MiniChatProps): ReactElement => {
  const audioTrigger = useAudioTrigger();

  const [recentMessages, setRecentMessages] = useState(messages);

  useEffect(() => {
    setRecentMessages(messages.filter((message) => !isMessageExpired(message.timestamp)));
  }, [messages]);

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>, messageId: string): void => {
    if (event.animationName.startsWith(styles.fadeOut)) {
      setRecentMessages((prevMessages) => prevMessages.filter((msg) => msg.messageId !== messageId));
    }
  };

  const handleSendMessage = useCallback(
    (input: string) => {
      if (input.trim() !== '') {
        audioTrigger.play({ name: 'menuClick' });
        // Send message to chat server via websocket
        chatCommunicationController.sendMessage(JSON.stringify(createMessage(userId, input, tableId, casinoId)));
      }
    },
    [audioTrigger, casinoId, tableId, userId]
  );

  return (
    <div data-testid="mini-chat" className={styles.miniChat}>
      <MessageInput
        cashierCallback={cashierCallback}
        isGameChatBlocked={isGameChatBlocked}
        isGameChatUnavailable={isGameChatUnavailable}
        isLiveSupportChatBlocked
        isMiniChat
        onSubmit={handleSendMessage}
        selectedTab={ChatTabType.GameChat}
      />
      <div data-testid="mini-chat-message-wrapper" className={styles.miniChat__messagesWrapper}>
        {recentMessages.map((message) => (
          <div
            key={message.messageId}
            className={styles.miniChat__message}
            onAnimationEnd={(event) => handleAnimationEnd(event, message.messageId)}
          >
            <MessageBubble currentUserId={currentUserId} isMiniChat {...message} />
          </div>
        ))}
      </div>
    </div>
  );
};
